import { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { endOfDay, startOfDay } from 'date-fns';

import { BottomSheet } from '@/components/common/BottomSheet';
import TargetSelectorOpenerButton from '@/components/common/TargetSelectorOpenerButton';
import { Popup, SingleTargetSelector } from '@/components';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { DetailedRoute } from '@/generated/model/detailedRoute';
import { getTargetRoutes } from '@/generated/client/routes/routes';
import { RoutesHeader } from './RoutesHeader';
import { RoutesDateSelect } from './RoutesDateSelect';
import { RoutesList } from './RoutesList';
import { RoutesResultHeader } from './RoutesResultHeader';

import { ctxPluto } from '@/contexts/PlutoContext';
import { ctxPopup } from '@/contexts/PopupContext';
import { ctxRoutes } from '@/contexts/RoutesCtx.tsx';
import { MinimalTarget } from '@/generated/model';
import { rethrowIfNotClientException } from '@/utilities/throwableUtils';
import { TabValue } from '@/types';

function Body() {
  const routesCtx = useContext(ctxRoutes);
  const plutoCtx = useContext(ctxPluto);

  useEffect(() => {
    if (routesCtx?.selectedTarget) routesCtx?.setTab(TabValue.dateSelect);
  }, []);

  function setSelectedTargetWrapper(target: MinimalTarget | undefined) {
    if (!routesCtx || !target) {
      return;
    }
    routesCtx.setSelectedTarget(target);
    routesCtx.setTab(TabValue.dateSelect);
    routesCtx.setSelectedDates(undefined);
    routesCtx.setRoutes(undefined);
    plutoCtx?.setSelectedRoutes([]);
  }

  function setRoutesWrapper(values: DetailedRoute[] | undefined) {
    if (!routesCtx) return;
    routesCtx.setRoutes(values);
    plutoCtx?.setSelectedRoutes([]);
    routesCtx.setIsBottomSheetOpen(true);
    routesCtx.setAdjustMap(true);
  }

  function setSelectedDatesWrapper(from: Date, to: Date) {
    if (!routesCtx) return;
    routesCtx.setSelectedDates({ from, to });
  }

  if (!routesCtx) return null;

  switch (routesCtx.tab) {
    case TabValue.targetSelect:
      return (
        <SingleTargetSelector
          setSelectedTarget={setSelectedTargetWrapper}
          selectedTarget={routesCtx.selectedTarget}
          searchText={routesCtx.searchText}
          setSearchText={routesCtx.setSearchText}
        />
      );
    case TabValue.dateSelect:
      return (
        <RoutesDateSelect
          selectedTarget={routesCtx.selectedTarget}
          setRoutes={setRoutesWrapper}
          setSelectedDates={setSelectedDatesWrapper}
        />
      );
    default:
      // ez lehet nem kell ?? nem kell h undefined lehessen ?
      return (
        <SingleTargetSelector
          setSelectedTarget={setSelectedTargetWrapper}
          selectedTarget={routesCtx.selectedTarget}
          searchText={routesCtx.searchText}
          setSearchText={routesCtx.setSearchText}
        />
      );
  }
}

function RoutesSecondary() {
  const routesCtx = useContext(ctxRoutes);
  const plutoCtx = useContext(ctxPluto);

  const today = new Date();
  const from = startOfDay(today);
  const to = endOfDay(today);

  useEffect(() => {
    routesCtx?.setRoutes([]);
  }, [routesCtx?.selectedTarget]);

  if (!routesCtx || !plutoCtx) return;

  async function setSelectedTargetWrapper(target: MinimalTarget | undefined) {
    if (!routesCtx || !plutoCtx || !target) {
      return;
    }

    routesCtx.setSelectedTarget(target);
    if (!routesCtx.selectedDates) routesCtx.setSelectedDates({ from, to });
  }

  if (!routesCtx) return null;
  return (
    <div className={`route-panel-target-selector ${routesCtx.routesLoading ? 'disabled' : ''}`}>
      <SingleTargetSelector
        setSelectedTarget={setSelectedTargetWrapper}
        selectedTarget={routesCtx.selectedTarget}
        searchText={routesCtx.searchText}
        setSearchText={routesCtx.setSearchText}
      />
    </div>
  );
}

export default function RoutesPopup() {
  const plutoCtx = useContext(ctxPluto);
  const routesCtx = useContext(ctxRoutes);
  const popupCtx = useContext(ctxPopup);
  const { t } = useTranslation();
  const [showTargetSelector, setShowTargetSelector] = useState(false);
  const [isRouteSelected, setIsRouteSelected] = useState(false);
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const selectedDatesRef = useRef<{ from: Date; to: Date }>(routesCtx?.selectedDates);
  const selectedTargetRef = useRef<MinimalTarget>(routesCtx?.selectedTarget);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    const isOpen = searchParams.get('bottomsheet');
    routesCtx?.setIsBottomSheetOpen(isOpen === 'true' && routesCtx.selectedTarget !== undefined);
    return () => routesCtx?.setIsBottomSheetOpen(false);
  }, []);

  useEffect(() => {
    const target: MinimalTarget | undefined = location.state?.target;
    if (!target) return;

    function filterDuplicate(routes: DetailedRoute[] | undefined) {
      if (!routes) return routes;
      const routesSorted = routes.sort((a, b) => a.startPosition.timeMicros - b.startPosition.timeMicros);

      let previous: number = -1;
      const routesFiltered = routesSorted.filter((r) => {
        if (r.startPosition.timeMicros <= previous) return false;
        previous = r.startPosition.timeMicros;
        return true;
      });

      return routesFiltered;
    }

    async function setTargetRoutes() {
      if (!routesCtx || !target) return;

      const today = new Date();
      const from = startOfDay(today);
      const to = endOfDay(today);
      routesCtx?.setSelectedDates({ from, to });

      popupCtx?.showLoader('circular');
      try {
        routesCtx.setSelectedTarget(target);
        const result = await getTargetRoutes(target.id, {
          fromTime: from.getTime() * 1000,
          toTime: to.getTime() * 1000,
        });
        const count = result.data.length;
        if (count === 0) {
          routesCtx?.setIsBottomSheetOpen(false);
          setShowTargetSelector(true);
        } else {
          routesCtx.setRoutes(filterDuplicate(result.data));
          routesCtx?.setIsBottomSheetOpen(true);
        }
      } catch (error) {
        rethrowIfNotClientException(error);
      } finally {
        popupCtx?.hideLoader();
      }
    }

    routesCtx?.setSelectedTarget(target);
    plutoCtx?.setSelectedRoutes([]);
    setTargetRoutes();
  }, []);

  useEffect(() => {
    plutoCtx?.setMode('route'); // show selectedTargets on the map
  }, [plutoCtx]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('bottomsheet', routesCtx?.isBottomSheetOpen ? 'true' : 'false');
    setSearchParams(params, {
      preventScrollReset: true,
    });

    if (routesCtx?.isBottomSheetOpen) {
      setShowTargetSelector(false);
    }
  }, [routesCtx?.isBottomSheetOpen]);

  useEffect(() => {
    if (
      !selectedDatesRef ||
      routesCtx?.selectedDates !== selectedDatesRef.current ||
      !selectedTargetRef ||
      routesCtx?.selectedTarget !== selectedTargetRef.current
    ) {
      routesCtx?.setHighlightedRoutes([]);
      selectedDatesRef.current = routesCtx?.selectedDates;
      selectedTargetRef.current = routesCtx?.selectedTarget;
    }

    if (routesCtx?.routes !== undefined) {
      setIsRouteSelected(true);
    } else {
      setIsRouteSelected(false);
    }
  }, [routesCtx?.routes]);

  if (!routesCtx) return;

  const bottomSheetKey = [
    routesCtx.selectedTarget?.id ?? '',
    routesCtx.selectedDates?.from.getTime() ?? '',
    routesCtx.selectedDates?.to.getTime() ?? '',
  ].join(':');

  return isPhoneOrHighScreen() ? (
    <>
      <TargetSelectorOpenerButton
        setOnClicked={() => {
          setShowTargetSelector(true);
          routesCtx.setIsBottomSheetOpen(false);
        }}
        isVehicleOrRouteSelected={isRouteSelected}
        title={t('mainMenu.routes')}
      />
      {showTargetSelector && (
        <Popup
          popupTitle={t('mainMenu.routes')}
          header={<RoutesHeader />}
          body={<Body />}
          footer={undefined}
          isFullScr={true}
          leftText={true}
          closePopup={async () => {
            setShowTargetSelector(false);
            if (routesCtx.routes && routesCtx.routes.length !== 0) {
              routesCtx.setIsBottomSheetOpen(true);
              routesCtx.setAdjustMap(true);
            }
          }}
        />
      )}
      {routesCtx.isBottomSheetOpen && (
        <BottomSheet
          key={bottomSheetKey}
          header={<RoutesResultHeader />}
          bottomSheetUp={routesCtx.isBottomSheetUp}
          setBottomSheetUp={routesCtx.setIsBottomSheetUp}
          routeTab={true}
        >
          <RoutesList topRef={summaryRef} />
        </BottomSheet>
      )}
    </>
  ) : (
    <>
      <RoutesSecondary />
      {routesCtx.routes && <RoutesList topRef={summaryRef} />}
    </>
  );
}
